import React, { useCallback, useMemo, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import { useStoreCart } from "./StoreCartContext";
import { useStoreAuth } from "./StoreAuthContext";
import { useTranslation } from "react-i18next";
import {
  resolveUrl,
  formatStorePricePlain,
  translateStoreCategory,
  localizedStoreProductName,
  isStoreArabicLang,
  isStoreProductOutOfStock,
  getStoreProductPriceDisplay,
} from "../../utils/storeUtils";
import { StorePriceDisplay } from "./components/StorePriceDisplay";
import "./Store.css";
import "./Wishlist.css";

function EmptyWishlistIllustration() {
  return (
    <div className="store-wishlist-empty-illus" aria-hidden="true">
      <div className="store-wishlist-empty-heart-wrap">
        <i className="fa-regular fa-heart store-wishlist-empty-heart" />
      </div>
      <div className="store-wishlist-empty-dots" role="presentation">
        <span className="store-wishlist-dot" />
        <span className="store-wishlist-dot" />
        <span className="store-wishlist-dot" />
      </div>
    </div>
  );
}

export default function Wishlist() {
  const { t, i18n } = useTranslation("global");
  const navigate = useNavigate();
  const apiBaseUrl = axios.defaults.baseURL || "";
  const { isAuthenticated, user } = useStoreAuth();
  const {
    wishlist,
    removeFromWishlist,
    addToCart,
    wishlistSyncing,
  } = useStoreCart();

  const [quantities, setQuantities] = useState({});
  const [sortBy, setSortBy] = useState("date");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [currentProducts, setCurrentProducts] = useState({});
  const [loadingCurrent, setLoadingCurrent] = useState(false);

  const isRtl = isStoreArabicLang(i18n.language);

  const sortOptions = useMemo(
    () => [
      {
        value: "date",
        label: t("store.wishlist.sortDate", { defaultValue: "Date added" }),
      },
      {
        value: "name-asc",
        label: t("store.wishlist.sortNameAsc", { defaultValue: "Name (A–Z)" }),
      },
      {
        value: "name-desc",
        label: t("store.wishlist.sortNameDesc", { defaultValue: "Name (Z–A)" }),
      },
      {
        value: "price-asc",
        label: t("store.wishlist.sortPriceAsc", {
          defaultValue: "Price (low to high)",
        }),
      },
      {
        value: "price-desc",
        label: t("store.wishlist.sortPriceDesc", {
          defaultValue: "Price (high to low)",
        }),
      },
    ],
    [t]
  );

  const resolveProductUrl = useCallback(
    (url) => resolveUrl(url, apiBaseUrl),
    [apiBaseUrl]
  );

  // Fetch current product data for price/stock visibility
  useEffect(() => {
    if (wishlist.length === 0) return;
    setLoadingCurrent(true);
    axios
      .get("/store/products")
      .then(({ data }) => {
        const map = {};
        (Array.isArray(data) ? data : []).forEach((p) => {
          if (p._id) map[p._id] = p;
        });
        setCurrentProducts(map);
      })
      .catch(() => setCurrentProducts({}))
      .finally(() => setLoadingCurrent(false));
  }, [wishlist.length]);

  const categories = useMemo(() => {
    const set = new Set();
    wishlist.forEach((p) => {
      if (p?.category) set.add(p.category);
    });
    return Array.from(set).sort();
  }, [wishlist]);

  const sortedAndFiltered = useMemo(() => {
    let list = [...wishlist];
    if (categoryFilter) {
      list = list.filter(
        (p) => (p?.category || "").toLowerCase() === categoryFilter.toLowerCase()
      );
    }
    if (sortBy === "date") return list;
    if (sortBy === "name-asc")
      return list.sort((a, b) =>
        localizedStoreProductName(a, i18n.language).localeCompare(
          localizedStoreProductName(b, i18n.language),
          i18n.language || undefined,
          { sensitivity: "base" }
        )
      );
    if (sortBy === "name-desc")
      return list.sort((a, b) =>
        localizedStoreProductName(b, i18n.language).localeCompare(
          localizedStoreProductName(a, i18n.language),
          i18n.language || undefined,
          { sensitivity: "base" }
        )
      );
    const sortFinal = (p) => getStoreProductPriceDisplay(p).finalPrice;
    if (sortBy === "price-asc")
      return list.sort((a, b) => sortFinal(a) - sortFinal(b));
    if (sortBy === "price-desc")
      return list.sort((a, b) => sortFinal(b) - sortFinal(a));
    return list;
  }, [wishlist, sortBy, categoryFilter, i18n.language]);

  const getQuantity = useCallback(
    (productId) => {
      const q = quantities[productId];
      return q !== undefined && Number(q) >= 1 ? Number(q) : 1;
    },
    [quantities]
  );

  const setQuantity = useCallback((productId, value) => {
    const num = Math.max(1, Math.min(999, Number(value) || 1));
    setQuantities((prev) => ({ ...prev, [productId]: num }));
  }, []);

  const handleAddToCart = useCallback(
    (product) => {
      const current = currentProducts[product._id];
      const inStock = current && !isStoreProductOutOfStock(current);
      if (!inStock) {
        toast.error(
          t("store.wishlist.toastOutOfStock", {
            defaultValue: "This item is currently out of stock.",
          })
        );
        return;
      }
      const qty = getQuantity(product._id);
      const merged = current ? { ...product, ...current } : product;
      for (let i = 0; i < qty; i++) addToCart(merged);
      const label = localizedStoreProductName(merged, i18n.language);
      toast.success(
        label
          ? t("store.toastProductAddedToCartQty", {
              name: label,
              qty,
              defaultValue: "{{name}} (×{{qty}}) added to cart.",
            })
          : t("store.toastProductAddedToCartGeneric", {
              defaultValue: "Product added to cart.",
            })
      );
    },
    [addToCart, getQuantity, currentProducts, i18n.language, t]
  );

  const handleRemove = useCallback(
    (product) => {
      removeFromWishlist(product._id);
      const label = localizedStoreProductName(product, i18n.language);
      toast.info(
        label
          ? t("store.toastProductRemovedWishlist", {
              name: label,
              defaultValue: "{{name}} removed from wishlist.",
            })
          : t("store.toastProductRemovedWishlistGeneric", {
              defaultValue: "Removed from wishlist.",
            })
      );
    },
    [removeFromWishlist, i18n.language, t]
  );

  const handleShareCopyLink = useCallback(() => {
    const url = window.location.origin + "/store/wishlist";
    navigator.clipboard
      .writeText(url)
      .then(() =>
        toast.success(
          t("store.wishlist.toastLinkCopied", {
            defaultValue: "Wishlist link copied to clipboard.",
          })
        )
      )
      .catch(() =>
        toast.error(
          t("store.wishlist.toastCopyFailed", {
            defaultValue: "Could not copy link.",
          })
        )
      );
  }, [t]);

  const handleShareEmail = useCallback(() => {
    const subject = encodeURIComponent(
      t("store.wishlist.emailSubject", { defaultValue: "My wishlist from Alramlaa" })
    );
    const productList =
      wishlist.length > 0
        ? wishlist
            .map(
              (p) =>
                `- ${localizedStoreProductName(p, i18n.language) || t("store.checkout.productAlt", { defaultValue: "Product" })} — ${formatStorePricePlain(getStoreProductPriceDisplay(p).finalPrice, i18n.language)}`
            )
            .join("\n")
        : t("store.wishlist.emailEmptyList", {
            defaultValue: "My saved favorites from Alramlaa Store.",
          });
    const intro = t("store.wishlist.emailBodyIntro", {
      defaultValue: "Here are the products I've saved:",
    });
    const outro = t("store.wishlist.emailBodyOutro", {
      defaultValue: "View the store:",
    });
    const body = encodeURIComponent(
      `${intro}\n\n${productList}\n\n${outro} ${window.location.origin}/store`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  }, [wishlist, i18n.language, t]);

  const getProductStatus = useCallback(
    (product) => {
      const current = currentProducts[product._id];
      const inStock = current && !isStoreProductOutOfStock(current);
      const saved = getStoreProductPriceDisplay(product);
      const live = getStoreProductPriceDisplay(current || product);
      const currentPrice = live.finalPrice;
      const priceChanged =
        current != null && Math.abs(live.finalPrice - saved.finalPrice) > 0.001;
      return { inStock, priceChanged, currentPrice };
    },
    [currentProducts]
  );

  const firstName = user?.fullName?.trim().split(/\s+/)[0] || "";

  if (wishlist.length === 0) {
    return (
      <section
        className="store-page store-wishlist-page"
        dir={isRtl ? "rtl" : "ltr"}
        lang={isRtl ? "ar" : "en"}
      >
        <div className="container">
          <div className="store-wishlist-inner">
            <Link to="/store" className="store-checkout-back">
              <i className="fa-solid fa-arrow-left" aria-hidden="true" />
              <span>
                {t("store.wishlist.backToStore", {
                  defaultValue: "Back to store",
                })}
              </span>
            </Link>
            <div className="store-empty store-wishlist-empty store-wishlist-empty-card">
              <EmptyWishlistIllustration />
              <h2 className="store-wishlist-title">
                {firstName
                  ? t("store.wishlist.emptyTitleNamed", {
                      name: firstName,
                      defaultValue: "Hi {{name}}! Your wishlist is empty",
                    })
                  : t("store.wishlist.emptyTitle", {
                      defaultValue: "Your wishlist is empty",
                    })}
              </h2>
              <p className="store-wishlist-empty-text">
                {t("store.wishlist.emptyBody", {
                  defaultValue:
                    "Save your favorite products by clicking the heart on any product. They'll show up here so you can buy or share them later.",
                })}
              </p>
              <Link to="/store" className="store-empty-reset store-wishlist-cta">
                {t("store.wishlist.exploreProducts", {
                  defaultValue: "Explore products",
                })}
              </Link>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className="store-page store-wishlist-page"
      dir={isRtl ? "rtl" : "ltr"}
      lang={isRtl ? "ar" : "en"}
    >
      <div className="container">
        <div className="store-wishlist-inner">
          <div className="store-wishlist-header">
            <div className="store-wishlist-header-bar">
              <Link
                to="/store"
                className="store-wishlist-back"
                aria-label={t("store.wishlist.backToStore", {
                  defaultValue: "Back to store",
                })}
                title={t("store.wishlist.backToStore", {
                  defaultValue: "Back to store",
                })}
              >
                <i className="fa-solid fa-arrow-left" aria-hidden="true" />
              </Link>
              <div className="store-wishlist-heading">
                <h1 className="store-wishlist-page-title">
                  <i
                    className="fa-solid fa-heart store-wishlist-title-heart"
                    aria-hidden="true"
                  />
                  {t("store.wishlist.pageTitle", { defaultValue: "My wishlist" })}
                  <span className="store-wishlist-count-pill">
                    {wishlist.length}
                  </span>
                </h1>
                <p className="store-wishlist-page-subtitle">
                  {wishlist.length === 1
                    ? t("store.wishlist.itemSaved", {
                        count: wishlist.length,
                        defaultValue: "{{count}} item saved",
                      })
                    : t("store.wishlist.itemsSaved", {
                        count: wishlist.length,
                        defaultValue: "{{count}} items saved",
                      })}
                  {wishlistSyncing && (
                    <span className="store-wishlist-syncing" aria-live="polite">
                      <i
                        className="fa-solid fa-circle-notch fa-spin"
                        aria-hidden="true"
                      />{" "}
                      {t("store.wishlist.syncing", { defaultValue: "Syncing…" })}
                    </span>
                  )}
                </p>
              </div>
              <div className="store-wishlist-controls">
                <select
                  id="wishlist-sort"
                  className="store-wishlist-select"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  aria-label={t("store.wishlist.sortAria", {
                    defaultValue: "Sort wishlist",
                  })}
                >
                  {sortOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {categories.length > 0 && (
                  <select
                    id="wishlist-filter"
                    className="store-wishlist-select"
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    aria-label={t("store.wishlist.filterAria", {
                      defaultValue: "Filter by category",
                    })}
                  >
                    <option value="">
                      {t("store.wishlist.allCategories", {
                        defaultValue: "All categories",
                      })}
                    </option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {translateStoreCategory(t, { label: cat })}
                      </option>
                    ))}
                  </select>
                )}
                <button
                  type="button"
                  className="store-wishlist-icon-btn"
                  onClick={handleShareCopyLink}
                  aria-label={t("store.wishlist.copyLinkAria", {
                    defaultValue: "Copy wishlist link",
                  })}
                  title={t("store.wishlist.copyLink", {
                    defaultValue: "Copy link",
                  })}
                >
                  <i className="fa-solid fa-link" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="store-wishlist-icon-btn"
                  onClick={handleShareEmail}
                  aria-label={t("store.wishlist.emailShareAria", {
                    defaultValue: "Share wishlist by email",
                  })}
                  title={t("store.wishlist.email", { defaultValue: "Email" })}
                >
                  <i className="fa-solid fa-envelope" aria-hidden="true" />
                </button>
              </div>
            </div>

            {!isAuthenticated && (
              <div className="store-wishlist-guest-prompt" role="status">
                <i className="fa-solid fa-cloud-arrow-up" aria-hidden="true" />
                <span>
                  {t("store.wishlist.guestPrompt", {
                    defaultValue:
                      "Sign in to save your wishlist across devices and keep it private.",
                  })}
                </span>
                <Link to="/store" className="store-wishlist-guest-link">
                  {t("store.wishlist.signIn", { defaultValue: "Sign in" })}
                </Link>
              </div>
            )}
          </div>

          {sortedAndFiltered.length === 0 ? (
            <div className="store-empty store-wishlist-empty store-wishlist-filter-empty">
              <p>
                {t("store.wishlist.filterEmpty", {
                  defaultValue: "No items match the selected category.",
                })}
              </p>
              <button
                type="button"
                className="store-empty-reset"
                onClick={() => setCategoryFilter("")}
              >
                {t("store.wishlist.showAll", { defaultValue: "Show all" })}
              </button>
            </div>
          ) : (
            <ul className="store-wishlist-grid">
              {sortedAndFiltered.map((product) => {
                const current = currentProducts[product._id];
                const forDisplay = { ...product, ...current };
                const displayName = localizedStoreProductName(
                  forDisplay,
                  i18n.language
                );
                const firstImg = product?.images?.[0] ?? product?.image;
                const imageUrl =
                  resolveProductUrl(firstImg?.url) ?? firstImg?.url ?? "";
                const imgAlt =
                  (isStoreArabicLang(i18n.language)
                    ? firstImg?.arAlt || firstImg?.alt
                    : firstImg?.alt || firstImg?.arAlt) ||
                  displayName ||
                  t("store.checkout.productAlt", { defaultValue: "Product" });
                const { inStock, priceChanged, currentPrice } =
                  getProductStatus(product);
                const currentPriceDisplay = (
                  <StorePriceDisplay amount={currentPrice} />
                );
                const qty = getQuantity(product._id);
                return (
                  <li
                    key={product._id}
                    className="store-wishlist-card"
                  >
                    <div className="store-wishlist-card-img-wrap">
                      <img
                        src={imageUrl}
                        alt={imgAlt}
                        loading="lazy"
                      />
                      {!inStock && (
                        <span
                          className="store-wishlist-badge store-wishlist-badge-out"
                          aria-label={t("store.wishlist.outOfStockAria", {
                            defaultValue: "Out of stock",
                          })}
                        >
                          {t("store.wishlist.outOfStockBadge", {
                            defaultValue: "Out of stock",
                          })}
                        </span>
                      )}
                      <button
                        type="button"
                        className="store-wishlist-card-remove store-weekly-wishlist is-active store-wishlist-heart-btn"
                        onClick={() => handleRemove(product)}
                        aria-label={t("store.wishlist.removeAria", {
                          name: displayName,
                          defaultValue: "Remove {{name}} from wishlist",
                        })}
                      >
                        <i className="fa-solid fa-heart" aria-hidden="true" />
                      </button>
                    </div>
                    <div className="store-wishlist-card-body">
                      <h3 className="store-wishlist-card-name">
                        {displayName}
                      </h3>
                      {product?.category && (
                        <p className="store-wishlist-card-category">
                          {translateStoreCategory(t, { label: product.category })}
                        </p>
                      )}
                      <div className="store-wishlist-price-row">
                        {inStock ? (
                          <>
                            <p className="store-wishlist-card-price">
                              {currentPriceDisplay}
                            </p>
                            {priceChanged && (
                              <p className="store-wishlist-price-was">
                                {t("store.priceWasPrefix", { defaultValue: "Was " })}
                                <StorePriceDisplay
                                  amount={getStoreProductPriceDisplay(product).finalPrice}
                                />
                              </p>
                            )}
                          </>
                        ) : null}
                      </div>
                      {loadingCurrent && !currentProducts[product._id] && (
                        <p className="store-wishlist-loading-price">
                          {t("store.wishlist.checkingPrice", {
                            defaultValue: "Checking price…",
                          })}
                        </p>
                      )}
                      <div className="store-wishlist-card-qty">
                        <label htmlFor={`wishlist-qty-${product._id}`}>
                          {t("store.wishlist.qty", { defaultValue: "Qty" })}
                        </label>
                        <input
                          id={`wishlist-qty-${product._id}`}
                          type="number"
                          min={1}
                          max={999}
                          value={qty}
                          onChange={(e) =>
                            setQuantity(product._id, e.target.value)
                          }
                          aria-label={t("store.wishlist.qtyAria", {
                            name: displayName,
                            defaultValue: "Quantity for {{name}}",
                          })}
                          disabled={!inStock}
                        />
                      </div>
                      <div className="store-wishlist-card-actions">
                        <button
                          type="button"
                          className="store-wishlist-add-btn"
                          onClick={() => handleAddToCart(product)}
                          disabled={!inStock}
                        >
                          {inStock
                            ? t("store.addToCart", { defaultValue: "Add to cart" })
                            : t("store.outOfStock", {
                                defaultValue: "Out of stock",
                              })}
                        </button>
                        <button
                          type="button"
                          className="store-wishlist-checkout-btn"
                          onClick={() => {
                            if (inStock) {
                              handleAddToCart(product);
                              navigate("/store/checkout");
                            }
                          }}
                          disabled={!inStock}
                        >
                          {t("store.wishlist.buyNow", {
                            defaultValue: "Buy now",
                          })}
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}
